/**
 * Transitions-gallery demos for the dashboard-render motion
 * primitives + the composed motion PATTERNS (motion-polish plan
 * §2.6 entries 2-6 and 8-10).
 *
 * Internal to this package: the builder's gallery merges these into the
 * "transitions" category next to the bi-components entries (tokens /
 * spinners / progress bar / shimmer). Every demo renders through
 * `TransitionDemo` (bi-components) — Replay button, reduced-motion
 * toggle, and the Used-in / How / Export metadata contract.
 *
 * The SWR demo is SYNTHETIC by design: the real stale-while-revalidate
 * cycle SHIPPED (GenericDashboardPage / PreviewModal), and this
 * mini-dashboard reproduces the same choreography (global hairline +
 * per-card dim + number morphs + table fade-over) with fake fetches so
 * the beat stays calibratable without a backend.
 *
 * IMPORTANT — what these demos must teach (decisión 2026-07-23, and the
 * gallery IS the contract): on a refetch the CHARTS DO NOT CROSSFADE.
 * `DashboardBody` passes `disabled` to the chart tier's `FadeSwap`
 * (DashboardBody.tsx ~1044). The refetch language is: numbers MORPH
 * (`NumberTween` over `MOTION.durBase`), lines REDRAW (`useLineDraw`
 * keyed by `lineDataSig`), bar/radial charts RE-GROW together
 * (`useGrowCycle`), tables cascade ONLY on their first landing, and the
 * dim appears only on slow fetches. `FadeSwap` stays in production for
 * tables, text blocks and the `compare` KPI card — nothing else.
 */
import { useRef, useState, type ReactNode } from "react";

import {
  GlobalProgressBar,
  MOTION,
  MeterBar,
  StatusChip,
  TransitionDemo,
  VerticalBar,
  ensureMotionStyles,
  usePrefersReducedMotion,
} from "@pervasivemind/bi-components";

import { FadeSwap } from "./FadeSwap";
import { ViewSlide } from "./ViewSlide";
import { HeightTransition } from "./HeightTransition";
import { NumberTween } from "./NumberTween";
import { RefreshOverlay } from "./RefreshOverlay";
// The accordion demo mounts the REAL table + panel (not a mock): the
// gallery is the contract, and a mock could drift from what ships.
// No cycle: `DataTable` imports `./motion/index`, which never imports
// this file.
import { DataTable, type ColumnDef } from "../DataTable";
import { RowDrillPanel } from "../RowDrillPanel";
import type { DashboardDataSource } from "../dashboard-data";
import type { RowDrillData, TableColumn } from "../types";

// ── shared demo data ────────────────────────────────────────

interface DemoDataset {
  id: string;
  bars: Array<{ label: string; value: number }>;
  rows: Array<{ seller: string; listings: number; compliance: string }>;
  kpiListings: number;
  kpiSellers: number;
}

const DATASET_A: DemoDataset = {
  id: "2026-Q1",
  bars: [
    { label: "Amazon", value: 4200 },
    { label: "ML", value: 3100 },
    { label: "Falabella", value: 1900 },
    { label: "Paris", value: 950 },
  ],
  rows: [
    { seller: "TechnoMax", listings: 1240, compliance: "92%" },
    { seller: "CompuGlobal", listings: 986, compliance: "88%" },
    { seller: "OfficePlus", listings: 733, compliance: "95%" },
  ],
  kpiListings: 9650,
  kpiSellers: 184,
};

const DATASET_B: DemoDataset = {
  id: "2026-Q2",
  bars: [
    { label: "Amazon", value: 5100 },
    { label: "ML", value: 2700 },
    { label: "Falabella", value: 2450 },
    { label: "Paris", value: 1200 },
  ],
  rows: [
    { seller: "CompuGlobal", listings: 1410, compliance: "91%" },
    { seller: "TechnoMax", listings: 1102, compliance: "94%" },
    { seller: "MegaStore", listings: 640, compliance: "83%" },
  ],
  kpiListings: 11460,
  kpiSellers: 209,
};

function DemoButton({
  onClick,
  children,
  disabled = false,
}: {
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-md border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-600 transition-colors hover:border-accent/50 hover:text-accent-dark disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function MiniTable({ rows }: { rows: DemoDataset["rows"] }): JSX.Element {
  return (
    <table className="w-full text-[11px]">
      <thead>
        <tr className="border-b border-neutral-200 text-left text-2xs uppercase tracking-wider text-neutral-600">
          <th className="py-1 pr-2 font-semibold">Seller</th>
          <th className="py-1 pr-2 text-right font-semibold">Listings</th>
          <th className="py-1 text-right font-semibold">MAP</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.seller} className="border-b border-neutral-100 text-neutral-700">
            <td className="py-1 pr-2">{r.seller}</td>
            <td className="py-1 pr-2 text-right tabular-nums">{r.listings.toLocaleString()}</td>
            <td className="py-1 text-right tabular-nums">{r.compliance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── 2. FadeSwap ─────────────────────────────────────────────

export function DemoFadeSwap(): JSX.Element {
  const [b, setB] = useState(false);
  const ds = b ? DATASET_B : DATASET_A;
  return (
    <TransitionDemo
      usedIn={[
        "Table row blocks after a refetch (DataTable — keyed by a content signature, never array identity)",
        "Dashboard text blocks when their value changes",
        "KPI cards of the compare variant (value + delta chip swap as one card)",
        "NOT charts: since 2026-07-23 the chart tier passes disabled — charts hard-swap and RE-GROW instead",
      ]}
      how={`Keyed fade-over: on swapKey change BOTH faces mount in the same commit (render-phase snapshot — never a frame of the new content alone); the old subtree stays mounted absolutely positioned on top (no layout shift) fading out while the incoming face updates in place, fully opaque — opacity only, ${MOTION.durBase}ms easeOut. The outgoing ghost is wrapped in SuppressEntrance, so its count-ups and grows do NOT restart toward the stale data.`}
      exportName="FadeSwap"
    >
      <div className="space-y-2">
        <DemoButton onClick={() => setB((v) => !v)}>
          Swap data ({ds.id})
        </DemoButton>
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <FadeSwap swapKey={ds.id}>
            <MiniTable rows={ds.rows} />
          </FadeSwap>
        </div>
      </div>
    </TransitionDemo>
  );
}

// ── 2b. ViewSlide ───────────────────────────────────────────

/** Reproduce el call site real: la celda cambia entre la tabla y el
 *  detalle de una fila, y "Volver" corre el mismo gesto invertido. */
export function DemoViewSlide(): JSX.Element {
  const [fila, setFila] = useState<string | null>(null);
  return (
    <TransitionDemo
      usedIn={[
        "Row-drill de las tablas en modo swap — row_drill_mode ausente o \"swap\", el DEFAULT (DashboardBody): la celda cambia entre la tabla y el detalle de la fila. En el acordeon (opt-in por reporte) NO hay cambio de vista — la tabla se queda y el detalle se despliega bajo la fila — asi que este gesto no corre",
        "El MISMO gesto invertido al volver — la direccion dice si avanzaste o retrocediste",
        "NO se usa para datos nuevos sobre la misma vista: eso es FadeSwap",
      ]}
      how={`Deslizamiento direccional: al cambiar viewKey las dos caras montan en el mismo commit (snapshot en fase de render, igual que FadeSwap) y recorren el 100% del ancho a la misma velocidad, asi que se tocan borde a borde y el fondo de la tarjeta no asoma por el medio — transform only, ${MOTION.durView}ms easeOut. \`forward\` sale por la izquierda, \`back\` por la derecha. El overflow se recorta SOLO durante el cruce, porque los popovers de filtro se desbordan de la celda a proposito. Con prefers-reduced-motion el cambio es instantaneo.`}
      exportName="ViewSlide"
    >
      <div className="space-y-2">
        <DemoButton onClick={() => setFila((v) => (v === null ? "CG_1" : null))}>
          {fila === null ? "Abrir detalle de una fila" : "← Volver"}
        </DemoButton>
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white p-3">
          <ViewSlide
            viewKey={fila === null ? "table" : `drill:${fila}`}
            direction={fila === null ? "back" : "forward"}
          >
            {fila === null ? (
              <MiniTable rows={DATASET_A.rows} />
            ) : (
              <div className="space-y-1.5 text-[11px]">
                <p className="text-sm font-semibold text-neutral-800">
                  Detalle de {fila}
                </p>
                <p className="text-neutral-500">
                  Una fila por fuente. La tabla quedo montada y oculta, asi
                  que volver es instantaneo — sin refetch, con pagina, orden
                  y busqueda intactos.
                </p>
              </div>
            )}
          </ViewSlide>
        </div>
      </div>
    </TransitionDemo>
  );
}

// ── 3. NumberTween ──────────────────────────────────────────

/** Mirrors the product call site (DashboardBody: `fromZero` +
 *  `durationMs={durEntrance}` + `delayMs={delayEntrance}`) so the demo
 *  shows BOTH phases: Replay = the entrance, "New value" = the morph. */
export function DemoNumberTween(): JSX.Element {
  const [value, setValue] = useState(9650);
  const [rate, setRate] = useState(92.4);
  return (
    <TransitionDemo
      usedIn={[
        "KPI card values on the display variants (card / compact / hero / iconstat / sparkline)",
        "Pre-formatted KPI strings that parse unambiguously (parseFormattedNumber → format re-emits the ORIGINAL string on landing)",
      ]}
      how={`Two phases, same as its twin useCountUp. ENTRANCE (mount-once): ${MOTION.delayEntrance}ms hold, then 0 → value over ${MOTION.durEntrance}ms easeOut — press Replay. LATER CHANGES (a filter refetch): morph from the number ON SCREEN to the new one over ${MOTION.durBase}ms with NO hold — parking ${MOTION.delayEntrance}ms on the previous number while the new data is already in hand would be stale data with no signal. Frames in flight are quantized to the target's decimals (the width never dances); the LANDED frame formats the value EXACTLY as received — no toFixed, no capped Intl — so the settled number is byte-identical to the static path (92.4 lands "92.4", never "92.4000"). Non-numeric strings pass through untouched (documented passthrough, not a fallback). Reduced motion / hidden tab / FadeSwap ghost render the final value directly.`}
      exportName="NumberTween"
    >
      <div className="space-y-2">
        <div className="flex gap-2">
          <DemoButton
            onClick={() => {
              setValue((v) => v + Math.round(Math.random() * 4000 - 1500));
              setRate((r) => Math.round((r + (Math.random() * 6 - 3)) * 10) / 10);
            }}
          >
            New value (morph)
          </DemoButton>
        </div>
        <div className="flex gap-3">
          <div className="w-44 rounded-lg border border-neutral-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-600">
              Active listings
            </div>
            <div className="mt-0.5 text-2xl font-bold text-neutral-800">
              <NumberTween
                fromZero
                durationMs={MOTION.durEntrance}
                delayMs={MOTION.delayEntrance}
                value={value}
              />
            </div>
          </div>
          <div className="w-44 rounded-lg border border-neutral-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-600">
              MAP compliance
            </div>
            <div className="mt-0.5 text-2xl font-bold text-neutral-800">
              {/* One decimal: watch the landed frame — it renders the
                  value as received, not a re-rounded one. */}
              <NumberTween
                fromZero
                durationMs={MOTION.durEntrance}
                delayMs={MOTION.delayEntrance}
                value={rate}
                format={(n) => `${n.toFixed(1)}%`}
              />
            </div>
          </div>
        </div>
      </div>
    </TransitionDemo>
  );
}

// ── 4. RefreshOverlay ───────────────────────────────────────

export function DemoRefreshOverlay(): JSX.Element {
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const simulate = (ms: number): void => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    setRefreshing(true);
    timerRef.current = setTimeout(() => setRefreshing(false), ms);
  };
  return (
    <TransitionDemo
      usedIn={["Any dashboard card while ITS data refetches (KPIs, charts, tables — per-block, PR-B)"]}
      how={`Content stays mounted and dims to 0.75 with a shimmer sweep on top (calibración 2026-07-23: 0.55 was a full luminance dip and read as parpadeo). Appears only after 300ms of refreshing — first-page-cache hits (~10ms) never flash it. After 5s a floating pill escalates: accent spinner + "Still querying the data…" + live elapsed seconds (heavy unfiltered aggregations can take 10-20s; a static dim reads as stuck). The pill announces its message ONCE through a polite live region that mounts empty; the seconds counter is aria-hidden (one announcement per second is noise, not information). Dim + pill keep working under reduced motion; the shimmer doesn't.`}
      exportName="RefreshOverlay"
    >
      <div className="space-y-2">
        <div className="flex gap-2">
          <DemoButton onClick={() => simulate(1200)}>Slow fetch (1.2s)</DemoButton>
          <DemoButton onClick={() => simulate(60)}>Cache hit (60ms — no flash)</DemoButton>
          <DemoButton onClick={() => simulate(12000)}>Heavy query (12s — long-wait pill)</DemoButton>
        </div>
        <RefreshOverlay refreshing={refreshing} blockPointer>
          <div className="rounded-lg border border-neutral-200 bg-white p-3">
            <MiniTable rows={DATASET_A.rows} />
          </div>
        </RefreshOverlay>
      </div>
    </TransitionDemo>
  );
}

// ── 5. Skeleton → content (first load only) ─────────────────

/** Inner body — rendered INSIDE TransitionDemo's MotionConfig so the
 *  reduced-motion toggle reaches the raw keyframe usage. */
function SkeletonToContentBody(): JSX.Element {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const replay = (): void => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    setLoaded(false);
    timerRef.current = setTimeout(() => setLoaded(true), 900);
  };
  return (
    <div className="space-y-2">
      <DemoButton onClick={replay}>Simulate first load</DemoButton>
      <div className="rounded-lg border border-neutral-200 bg-white p-3">
        {!loaded ? (
          <div className="space-y-2">
            <div className={`h-4 w-2/5 rounded bg-neutral-200/70 ${reduced ? "" : "animate-pulse"}`} />
            <div className={`h-16 w-full rounded-lg bg-neutral-200/70 ${reduced ? "" : "animate-pulse"}`} />
          </div>
        ) : (
          <div
            style={
              reduced
                ? undefined
                : { animation: `pmFadeIn ${MOTION.durBase}ms ${MOTION.easeOut}` }
            }
          >
            <MiniTable rows={DATASET_A.rows} />
          </div>
        )}
      </div>
    </div>
  );
}

export function DemoSkeletonToContent(): JSX.Element {
  return (
    <TransitionDemo
      usedIn={["FIRST load only (nothing to show yet) — DashboardSkeleton runs the same grid simulator as the body. Refetches use RefreshOverlay instead (SWR, plan §1.1)."]}
      how={`Grey placeholder sized exactly like the content (grid simulator), then the real content fades in (${MOTION.durBase}ms easeOut) when data lands. Never used again after first paint.`}
      exportName="DashboardSkeleton"
    >
      <SkeletonToContentBody />
    </TransitionDemo>
  );
}

// ── 6. SWR full cycle (the star demo — synthetic until PR-B) ─

export function DemoSwrCycle(): JSX.Element {
  const [ds, setDs] = useState<DemoDataset>(DATASET_A);
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const changeFilter = (): void => {
    if (refreshing) return;
    setRefreshing(true);
    timerRef.current = setTimeout(() => {
      setDs((cur) => (cur.id === DATASET_A.id ? DATASET_B : DATASET_A));
      setRefreshing(false);
    }, 1100);
  };
  return (
    <TransitionDemo
      usedIn={["The whole dashboard on every filter / cross-filter change (portal GenericDashboardPage + builder Preview — shipped; this card is a synthetic replica with fake fetches)"]}
      how="Stale-while-revalidate: old content STAYS visible (no skeleton swap, no unmount — the skeleton is first load only). Global hairline while anything is in flight + per-card dim only past 300ms; when data lands, each block transitions on ITS OWN arrival: KPI numbers MORPH over durBase, the chart hard-swaps and RE-GROWS (no crossfade — decisión 2026-07-23), the table fades over. A failed refresh keeps the old data but marks it (BlockStale ring + badge) — stale is never indistinguishable from current."
      exportName="GlobalProgressBar + RefreshOverlay + NumberTween + useGrowCycle + FadeSwap"
    >
      <div className="space-y-2">
        <DemoButton onClick={changeFilter} disabled={refreshing}>
          {refreshing ? "Fetching…" : `Change filter (→ ${ds.id === DATASET_A.id ? DATASET_B.id : DATASET_A.id})`}
        </DemoButton>
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <GlobalProgressBar active={refreshing} fixed={false} ariaLabel="Dashboard refreshing" />
          <div className="space-y-3 p-3">
            <div className="grid grid-cols-2 gap-3">
              <RefreshOverlay refreshing={refreshing}>
                <div className="rounded-lg border border-neutral-200 p-2.5">
                  <div className="text-2xs font-semibold uppercase tracking-wider text-neutral-600">Listings</div>
                  <div className="text-xl font-bold text-neutral-800">
                    <NumberTween
                      fromZero
                      durationMs={MOTION.durEntrance}
                      delayMs={MOTION.delayEntrance}
                      value={ds.kpiListings}
                    />
                  </div>
                </div>
              </RefreshOverlay>
              <RefreshOverlay refreshing={refreshing}>
                <div className="rounded-lg border border-neutral-200 p-2.5">
                  <div className="text-2xs font-semibold uppercase tracking-wider text-neutral-600">Sellers</div>
                  <div className="text-xl font-bold text-neutral-800">
                    <NumberTween
                      fromZero
                      durationMs={MOTION.durEntrance}
                      delayMs={MOTION.delayEntrance}
                      value={ds.kpiSellers}
                    />
                  </div>
                </div>
              </RefreshOverlay>
            </div>
            {/* NO FadeSwap here: the chart tier passes `disabled` in
                DashboardBody. The plot hard-swaps and the bars re-grow
                together (useGrowCycle, keyed by lineDataSig). */}
            <RefreshOverlay refreshing={refreshing}>
              <VerticalBar data={ds.bars} height={140} />
            </RefreshOverlay>
            <RefreshOverlay refreshing={refreshing}>
              <FadeSwap swapKey={ds.id}>
                <MiniTable rows={ds.rows} />
              </FadeSwap>
            </RefreshOverlay>
          </div>
        </div>
      </div>
    </TransitionDemo>
  );
}

// ── 8. Drill in-place (exhibits the shipped PR #54 motion) ──

function DrillInPlaceBody(): JSX.Element {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();
  const [open, setOpen] = useState<string | null>(null);
  return (
      <div className="rounded-lg border border-neutral-200 bg-white p-3">
        {open === null ? (
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-2xs uppercase tracking-wider text-neutral-600">
                <th className="py-1 font-semibold">Seller (click a row)</th>
                <th className="py-1 text-right font-semibold">Listings</th>
              </tr>
            </thead>
            <tbody>
              {DATASET_A.rows.map((r) => (
                <tr
                  key={r.seller}
                  onClick={() => setOpen(r.seller)}
                  className="cursor-pointer border-b border-neutral-100 text-neutral-700 hover:bg-accent/10"
                >
                  <td className="py-1.5">{r.seller}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.listings.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // pmRiseIn = the standardized pmRowDrillIn (opacity + 4px rise)
          <div style={reduced ? undefined : { animation: `pmRiseIn 150ms ${MOTION.easeOut}` }}>
            <div className="flex items-center gap-2 border-b border-neutral-100 pb-2">
              <button
                type="button"
                onClick={() => setOpen(null)}
                className="rounded-md border border-neutral-200 px-2 py-0.5 text-2xs font-semibold text-neutral-600 hover:border-accent/50 hover:text-accent-dark"
              >
                ← Back
              </button>
              <span className="text-[11px] text-neutral-500">
                Sellers <span className="text-neutral-300">›</span>{" "}
                <span className="font-semibold text-neutral-700">{open}</span>
              </span>
            </div>
            <div className="pt-2 text-[11px] text-neutral-500">
              Drill detail for <span className="font-semibold text-neutral-700">{open}</span> — same
              cell, same size, table still mounted underneath.
            </div>
          </div>
        )}
      </div>
  );
}

export function DemoDrillInPlace(): JSX.Element {
  return (
    <TransitionDemo
      usedIn={[
        "Row-drill of every table whose row_drill_mode is absent or \"swap\" — the DEFAULT (PR #54 design): clicking a row swaps the CELL's content for the drill panel (RowDrillPanel variant=\"swap\"), with the ViewSlide gesture. The accordion card next to this one is the per-report opt-in",
      ]}
      how={
        "The panel enters with opacity 0→1 + translateY(4px)→0 over 150ms easeOut (the shipped pmRowDrillIn keyframe, standardized as pmRiseIn). The cell never changes size; the table stays mounted underneath — Back restores instantly. Since 2026-09-09 the mode is chosen per report in the builder inspector («Cómo se muestra el detalle»); a table that omits row_drill_mode gets this one."
      }
      exportName="RowDrillPanel variant=swap (dashboard-render)"
    >
      <DrillInPlaceBody />
    </TransitionDemo>
  );
}

// ── 8b. Drill accordion (row_drill_mode: "accordion", the per-report opt-in since 2026-09-09) ──
//
// The real `DataTable` + `RowDrillPanel variant="accordion"`, wired the
// way `DashboardBody.DashboardTable` wires them: a bounded table (capPx
// → the rows zone scrolls), one active row, the detail mounted in an
// expansion row UNDER it inside that same scrolling zone, capped so row
// + detail always fit. The card never grows — resize nothing, scroll.

const ACCORDION_COLUMNS: ColumnDef[] = [
  { key: "seller", label: "Seller", format: "text" },
  { key: "listings", label: "Listings", format: "number" },
  { key: "compliance", label: "MAP", format: "text" },
  { key: "country", label: "Country", format: "text" },
  { key: "channel", label: "Channel", format: "text" },
];

/** The same columns in the config shape the panel's chips read. Every
 *  demo column declares a format, hence the plain cast. */
const ACCORDION_PANEL_COLUMNS: TableColumn[] = ACCORDION_COLUMNS.map((c) => ({
  column: c.key,
  key: c.key,
  label: c.label,
  format: c.format as string,
}));

const ACCORDION_ROWS: Array<Record<string, unknown>> = [
  ...DATASET_A.rows,
  { seller: "MegaStore", listings: 640, compliance: "83%" },
  { seller: "DigitalHub", listings: 512, compliance: "90%" },
  { seller: "ElectroCity", listings: 488, compliance: "79%" },
  { seller: "NetShop", listings: 301, compliance: "97%" },
  { seller: "ByteMarket", listings: 214, compliance: "86%" },
].map((r, i) => ({
  ...r,
  country: ["CL", "MX", "CO", "PE", "AR"][i % 5],
  channel: i % 2 === 0 ? "Marketplace" : "Retail",
}));

/** Level-1 payload: the seller's listings by week. */
function accordionPayload(seller: string): RowDrillData {
  return {
    drill_id: "drill_seller",
    title: { en: "Weekly listings", es: "Publicaciones por semana" },
    charts: [],
    table: {
      rows: Array.from({ length: 8 }, (_, i) => ({
        Week: `2026-W${String(30 + i).padStart(2, "0")}`,
        Listings: 900 - i * 37 + seller.length,
        "MAP %": `${(80 + ((i * 7) % 15)).toFixed(0)}%`,
      })),
      total_count: 8,
    },
  };
}

/** Level-2 (nested) source: a click on a week inside the detail. The
 *  delay is deliberate — it shows the loading skeleton the real thing
 *  shows. */
const ACCORDION_SOURCE: DashboardDataSource = {
  fetchTableData: async () => ({ rows: [], total_count: 0 }),
  fetchDrill: async () => ({ drill_id: "x", kpis: [], charts: [] }),
  fetchRowDrill: ({ rowValue }) =>
    new Promise((resolve) => {
      setTimeout(
        () =>
          resolve({
            drill_id: "drill_week",
            charts: [],
            table: {
              rows: [
                { SKU: `${rowValue}-A1`, Units: 41, Price: "$129.990" },
                { SKU: `${rowValue}-B7`, Units: 18, Price: "$89.990" },
                { SKU: `${rowValue}-C3`, Units: 7, Price: "$249.990" },
              ],
              total_count: 3,
            },
          }),
        450,
      );
    }),
};

function DrillAccordionBody(): JSX.Element {
  const [active, setActive] = useState<string | null>(null);
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-neutral-500">
        Haz clic en una fila. La tarjeta mide lo mismo antes y despues —
        lo que crece es lo que scrollea.
      </p>
      <DataTable
        capPx={300}
        rows={ACCORDION_ROWS}
        totalCount={ACCORDION_ROWS.length}
        sortBy="seller"
        sortDir="asc"
        onSort={() => undefined}
        page={1}
        pageSize={ACCORDION_ROWS.length}
        onPageChange={() => undefined}
        columns={ACCORDION_COLUMNS}
        title="Sellers"
        subtitle={`${ACCORDION_ROWS.length} rows`}
        onRowClick={(value) => setActive((cur) => (cur === value ? null : value))}
        rowDrillKey="seller"
        activeRowValue={active}
        renderRowExpansion={({ maxPx }) =>
          active === null ? null : (
            <RowDrillPanel
              variant="accordion"
              capPx={maxPx}
              tableTitle="Sellers"
              rowValue={active}
              row={ACCORDION_ROWS.find((r) => r.seller === active) ?? null}
              columns={ACCORDION_PANEL_COLUMNS}
              rowDrillKey="seller"
              data={accordionPayload(active)}
              loading={false}
              error={null}
              onClose={() => setActive(null)}
              dataSource={ACCORDION_SOURCE}
              activeFilters={{}}
              nestedTableId="tbl_weeks"
              nestedDrillTarget="drill_week"
              nestedDrillParam="Week"
            />
          )
        }
      />
    </div>
  );
}

export function DemoDrillAccordion(): JSX.Element {
  return (
    <TransitionDemo
      usedIn={[
        "Row-drill de toda tabla con row_drill_target y row_drill_mode: \"accordion\" EXPLICITO (opt-in por reporte desde 2026-09-09; el default sigue siendo swap, la carta de al lado) — DashboardBody.DashboardTable monta DataTable.renderRowExpansion → RowDrillPanel variant=\"accordion\"",
        "Escritorio (celda de grilla), movil (capPx) y pantalla completa: el acordeon vive DENTRO de la zona de filas, asi que funciona en los tres sin cambiar de tamano",
        "Nivel 2 anidado: clic en una fila del detalle abre el segundo nivel en el mismo acordeon; «Volver» retrocede un nivel, × cierra",
      ]}
      how={`La fila queda visible y debajo se inserta un <tr data-row-expansion> con colSpan de todas las columnas, DENTRO de la zona [data-rows-zone] que ya scrollea (overflow-y-auto). La celda de la grilla no cambia ni un pixel: lo que crece es el contenido scrolleable. El detalle tiene techo = alto de la zona − cabecera (27px) − 2 filas (48px), minimo 140px (expansionMaxPx en DataTable.tsx), con scroll propio; al abrir y al asentar la data se hace scrollIntoView({block:"nearest"}) del detalle y de la fila, en ese orden. Su cuerpo es position:sticky; left:0 con ancho = ancho VISIBLE de la zona, asi que en una tabla mas ancha que la celda el detalle no se va con las columnas al scrollear horizontal. Estado abierto = glifo ↳ + palabra «Detalle de fila» + la pildora de la fila en «Cerrar detalle» (forma y texto, no solo color). Entra con pmRowDrillIn (150ms ${MOTION.easeOut}). Se activa por tabla en el inspector del builder («Cómo se muestra el detalle» → «Acordeón bajo la fila»); sin ese campo la tabla usa el reemplazo de la carta de al lado.`}
      exportName="DataTable + RowDrillPanel variant=accordion (dashboard-render)"
    >
      <DrillAccordionBody />
    </TransitionDemo>
  );
}

// ── 9. Chart data-change — NO crossfade, the marks re-grow ──

export function DemoChartDataChange(): JSX.Element {
  const [b, setB] = useState(false);
  const ds = b ? DATASET_B : DATASET_A;
  return (
    <TransitionDemo
      usedIn={[
        "Every chart of the bar / radial family on a filter change (portal + builder Preview)",
        "Historical note: the tier-1 whole-plot crossfade was tried and TURNED OFF on 2026-07-23 — the chart tier's FadeSwap ships with `disabled`",
      ]}
      how={`The plot does NOT crossfade. The data hard-swaps and the MARKS re-grow: the chart owns one grow cycle (useGrowCycle, keyed by lineDataSig of the plotted values) and on a key change every mark — survivors and newcomers in the same wave — resets to zero for a single painted frame and grows again over ${MOTION.durEntrance}ms easeOut. Never per-item (that mixed old and new states while filtering). Same data ⇒ same key ⇒ nothing replays. Line-family charts redraw their stroke instead (useLineDraw); axes and labels never animate.`}
      exportName="useGrowCycle + EntranceGroup (FadeSwap disabled for charts)"
    >
      <div className="space-y-2">
        <DemoButton onClick={() => setB((v) => !v)}>Change data ({ds.id})</DemoButton>
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <VerticalBar data={ds.bars} height={160} />
        </div>
      </div>
    </TransitionDemo>
  );
}

// ── 10. Anti-jump: ring selection vs border (didactic) ──────

function SelectableChips({ mode }: { mode: "border" | "ring" }): JSX.Element {
  const [sel, setSel] = useState<string>("ML");
  return (
    <div className="flex flex-wrap gap-2">
      {["Amazon", "ML", "Falabella"].map((c) => {
        const active = sel === c;
        return (
          <button
            key={c}
            type="button"
            onClick={() => setSel(c)}
            className={
              mode === "border"
                ? `rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                    active
                      ? "border-2 border-accent text-accent-dark"
                      : "border border-neutral-200 text-neutral-600"
                  }`
                : `rounded-md border border-neutral-200 px-2.5 py-1 text-[11px] font-semibold transition-shadow ${
                    active
                      ? "text-accent-dark shadow-[inset_0_0_0_1.5px_rgb(var(--bi-accent))]"
                      : "text-neutral-600"
                  }`
            }
          >
            {c}
          </button>
        );
      })}
    </div>
  );
}

/** PR-C addition — the FilterBar trigger fix, demoed. Picking a long
 *  value must never widen the trigger (which re-wraps the bar and
 *  jumps the whole dashboard): the value truncates inside the
 *  flex-determined width. The "bad" variant sizes the trigger by its
 *  content, which is exactly what re-wrapped the real banner. */
function StableTriggerDemo({ stable }: { stable: boolean }): JSX.Element {
  const LONG = "Very Long Seller Storefront Name S.A. de C.V. (MX)";
  const [value, setValue] = useState<string>("All");
  return (
    <div className="flex w-full max-w-md items-end gap-2">
      <div className={`space-y-0.5 ${stable ? "min-w-0 flex-1 basis-36" : "shrink-0"}`}>
        <span className="block text-2xs font-semibold uppercase tracking-wide text-neutral-500">
          Seller
        </span>
        <button
          type="button"
          onClick={() => setValue((v) => (v === "All" ? LONG : "All"))}
          className={`flex h-7 items-center justify-between gap-1 rounded-lg border border-neutral-300 bg-white px-2 text-xs font-medium text-neutral-700 hover:bg-neutral-50 ${
            stable ? "w-full" : "whitespace-nowrap"
          }`}
        >
          <span className={stable ? "min-w-0 truncate" : ""}>{value}</span>
          <span className="shrink-0 text-neutral-400">▾</span>
        </button>
      </div>
      <div className="min-w-0 flex-1 basis-36 space-y-0.5">
        <span className="block text-2xs font-semibold uppercase tracking-wide text-neutral-500">
          Neighbor
        </span>
        <div className="flex h-7 w-full items-center rounded-lg border border-neutral-300 bg-white px-2 text-xs text-neutral-400">
          All
        </div>
      </div>
    </div>
  );
}

export function DemoAntiJump(): JSX.Element {
  return (
    <TransitionDemo
      usedIn={["Every selected/active state: cross-filter cells (color-only active state), KPI drill active, filter chips", "FilterBar triggers: truncated value + permanent 'Clear all' slot (PR-C)"]}
      how="Selection = inset ring / box-shadow (paints INSIDE the box — zero layout impact) and color-only text states. Never an added border (+1px per side resizes the element) and never a width that follows content: the FilterBar trigger truncates the selected value inside its flex width. Click around and watch the bad rows nudge."
      exportName="rule: ring/box-shadow, never additive border; truncate, never widen"
    >
      <div className="space-y-3">
        <div>
          <div className="mb-1 text-2xs font-bold uppercase tracking-wider text-red-400">
            Bad — border grows the box (neighbors shift)
          </div>
          <SelectableChips mode="border" />
        </div>
        <div>
          <div className="mb-1 text-2xs font-bold uppercase tracking-wider text-emerald-500">
            Good — inset ring, box size constant
          </div>
          <SelectableChips mode="ring" />
        </div>
        <div>
          <div className="mb-1 text-2xs font-bold uppercase tracking-wider text-red-400">
            Bad — trigger widens with the selected value (click it)
          </div>
          <StableTriggerDemo stable={false} />
        </div>
        <div>
          <div className="mb-1 text-2xs font-bold uppercase tracking-wider text-emerald-500">
            Good — value truncates, trigger width constant (shipped fix)
          </div>
          <StableTriggerDemo stable />
        </div>
      </div>
    </TransitionDemo>
  );
}

// ── HeightTransition (primitive #4's sibling — real height moves) ──

export function DemoHeightTransition(): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  return (
    <TransitionDemo
      usedIn={["The few legitimate height changes: accordions, AI notes, expanding detail blocks"]}
      how={`ResizeObserver measures the content; the wrapper animates its height between measurements (${MOTION.durSlow}ms easeInOut). Everything else in the system is opacity/transform only — this is the single explicit escape hatch.`}
      exportName="HeightTransition"
    >
      <div className="space-y-2">
        <DemoButton onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Collapse" : "Expand"}
        </DemoButton>
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <HeightTransition>
            <div className="text-[11px] text-neutral-600">
              Quarterly compliance summary.
              {expanded && (
                <p className="mt-2 text-neutral-500">
                  Expanded detail: MAP compliance improved 4.2pp QoQ driven by the top-10
                  sellers; 3 marketplaces remain below the 90% goal. The height of this
                  card animated instead of snapping.
                </p>
              )}
            </div>
          </HeightTransition>
        </div>
      </div>
    </TransitionDemo>
  );
}

// ── Scorecard meters (epic v3) ──────────────────────────────

const SCORE_BANDS = [
  { min: 95, tone: "good" as const },
  { min: 85, tone: "warn" as const },
  { min: Number.NEGATIVE_INFINITY, tone: "bad" as const },
];
const METER_SETS = [
  [
    { label: "Amazon", value: 98 },
    { label: "Costco", value: 91 },
    { label: "Best Buy", value: 72 },
  ],
  [
    { label: "Amazon", value: 88 },
    { label: "Costco", value: 96 },
    { label: "Best Buy", value: 84 },
  ],
];

export function DemoScorecardMeters(): JSX.Element {
  const [set, setSet] = useState(0);
  const rows = METER_SETS[set] ?? METER_SETS[0]!;
  return (
    <TransitionDemo
      usedIn={[
        "Regional health rows",
        "per-marketplace meters inside a country card",
        "the country card's overall health bar",
      ]}
      how={`The fill is full-width and SCALED (transform: scaleX), never width-animated — transform is compositor-only, so a meter easing to its new value on a filter change costs zero layout. ${MOTION.durBase}ms easeOut, off under reduced motion. The value label counts with useCountUp (entrance from zero, then morphs) formatted by the SAME deterministic formatter as the static path, so the landed frame is byte-identical. The ARIA channel never publishes an intermediate: aria-valuenow / aria-valuetext are built from the REAL final value — a reader announcing "61%" on the way to 94% would be the same lie as a frozen number on screen. The BAND is re-resolved from the authored thresholds on every value, so the colour and the number can never disagree, and it is also spelled out in the accessible name (Pablo is red-green colourblind: never colour alone).`}
      exportName="MeterBar"
    >
      <div className="space-y-2">
        <DemoButton onClick={() => setSet((v) => (v === 0 ? 1 : 0))}>
          New week
        </DemoButton>
        <div className="space-y-3 rounded-lg border border-neutral-200 bg-white p-3">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center gap-2">
              <MeterBar
                className="flex-1"
                label={row.label}
                labelWidth={64}
                value={row.value}
                toneSpec={{ thresholds: SCORE_BANDS }}
                showValue
                ariaLabel={`${row.label} health`}
                toneLabel="banded"
              />
              <StatusChip
                label={`${row.value}/100`}
                value={row.value}
                toneSpec={{ thresholds: SCORE_BANDS }}
                size="xs"
                toneLabel="banded"
              />
            </div>
          ))}
        </div>
      </div>
    </TransitionDemo>
  );
}
